import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import {SuccessModal} from '@ohif/ui';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@custom/store/store';
import { shareReportWhatsapp } from '@custom/rtk-apis/report/shareReportWhatsapp';
import { revertStatus } from '@custom/store/slices/report/shareReportWhatsappSlice';
import { servicesManager } from '../../../platform/app/src/App';
import { Loader } from '@ohif/ui';
import { ShareModalProps } from '@custom/Utils/constants/interfaces';

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, reportUrl, reportDet }) => {
    const { uiNotificationService } = servicesManager.services;
    const dispatch = useDispatch<AppDispatch>()
    const { status, loading, error, msg, } = useSelector((state: RootState) => state.shareReportWhatsappSliceResponse);

    const [formValues, setFormValues] = useState(null);
    const [isSuccessOpen, setIsSuccessOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('Report shared successfully!');
    const [isError, setIsError] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const initialValues = {
        patientName: '',
        whatsappNumber: '',
        reportUrl: reportUrl,  // Passed from props, non-editable
    };

    const handleCancel = () => {
        onClose();
    };

    const modalStyle = {
        width: '622px',
        height: '480px',
        display: 'grid'
    };

    const formstyle = {
        display: "grid",
        gap: "1.2rem",
    }
    const tittle = {
        fontSize: '24px',
        fontWeight: 600,
        lineHeight: '32px'
    }

    const handleSave = (values: any) => {
        const whatsappNumber = `${reportDet?.hospitalDetail?.whatsappCountryCode}${reportDet?.hospitalDetail?.whatsapp}`;
        const reportData = {
            reportId: reportDet?._id,
            reportNumber: reportDet?.reportNumber,
            phoneNumber: whatsappNumber
        };
        dispatch(shareReportWhatsapp(reportData));
    };

    useEffect(() => {
        if (status === 'SUCCESS') {
            onClose();
            setIsSuccessOpen(true);
            timeoutRef.current = setTimeout(() => {
                setIsSuccessOpen(false);
                dispatch(revertStatus())
            }, 2000);
        } else if (status == 'ERROR') {

            onClose();
            uiNotificationService.show({
                title: 'Share Report',
                message: msg,
                type: 'error',
            });
            onClose();
            dispatch(revertStatus());
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [status, onClose, dispatch, msg]);

    if (!isOpen && !isSuccessOpen) return null;

    return (
        <>
            {/* Main Share Modal */}
            {isOpen && (
                <div className="modal-overlay fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
                    <div className="modal-container bg-white p-6 rounded-lg shadow-lg" style={modalStyle}>
                        <div className="flex justify-between border-b pb-2" style={{ borderBottom: "1px solid #D2D6DB" }}>
                            <h2 className="modal-heading" style={tittle}>Share Report!</h2>
                            <div className="text-xl cursor-pointer" onClick={onClose}>X</div>
                        </div>
                        {loading ? (<Loader isLoaderOpen={loading} />) : (
                            <Formik
                                initialValues={initialValues}
                                // validationSchema={validationSchema}
                                onSubmit={handleSave}
                            >
                                {({ handleChange, handleBlur, touched, errors }) => (
                                    <Form>
                                        <div className="mt-4" style={formstyle}>
                                            {/* Patient Name Field */}
                                            <div className="mb-4">
                                                <label className="text-left label-text-font">Patient Name</label>
                                                <Field
                                                    name="patientName"
                                                    type="text"
                                                    className="p-2 border rounded w-full text-font"
                                                    placeholder="Enter patient name"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={reportDet?.name}
                                                    disabled
                                                />
                                                {/* {touched.patientName && errors.patientName && (
                                                <ErrorMessage name="patientName" component="div" className="error text-error" />
                                            )} */}
                                            </div>

                                            {/* WhatsApp Number Field */}
                                            <div className="mb-4">
                                                <label className="text-left label-text-font">WhatsApp Number</label>
                                                <div className="relative flex items-center">
                                                    <span className="absolute left-2 text-xl ml-2 pr-2" style={{ borderRight: "1px solid #D9D9D9" }}>
                                                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M18.7049 3.19687C16.6473 1.13437 13.9071 0 10.9951 0C4.98437 0 0.0933036 4.89107 0.0933036 10.9018C0.0933036 12.8219 0.594196 14.6978 1.54688 16.3527L0 22L5.77991 20.4826C7.37098 21.3518 9.16339 21.8085 10.9902 21.8085H10.9951C17.0009 21.8085 22 16.9174 22 10.9067C22 7.99464 20.7625 5.25937 18.7049 3.19687ZM10.9951 19.9719C9.36473 19.9719 7.76875 19.5348 6.37902 18.7098L6.05 18.5134L2.62232 19.4121L3.53571 16.0679L3.31964 15.7241C2.41116 14.2804 1.93482 12.6156 1.93482 10.9018C1.93482 5.90759 6.00089 1.84152 11 1.84152C13.421 1.84152 15.6946 2.78437 17.4036 4.49821C19.1125 6.21205 20.1634 8.48571 20.1585 10.9067C20.1585 15.9058 15.9893 19.9719 10.9951 19.9719ZM15.9647 13.1853C15.6946 13.0478 14.354 12.3897 14.1036 12.3013C13.8531 12.208 13.6714 12.1638 13.4897 12.4388C13.308 12.7138 12.7875 13.3228 12.6254 13.5094C12.4683 13.6911 12.3062 13.7156 12.0362 13.5781C10.4353 12.7777 9.38438 12.1491 8.32857 10.3371C8.04866 9.8558 8.60848 9.89018 9.12902 8.84911C9.21741 8.66741 9.17321 8.51027 9.10446 8.37277C9.03571 8.23527 8.49063 6.89464 8.26473 6.34955C8.04375 5.8192 7.81786 5.89286 7.65089 5.88304C7.49375 5.87321 7.31205 5.87321 7.13036 5.87321C6.94866 5.87321 6.65402 5.94196 6.40357 6.21205C6.15312 6.48705 5.45089 7.14509 5.45089 8.48571C5.45089 9.82634 6.42813 11.1228 6.56071 11.3045C6.69821 11.4862 8.4808 14.2362 11.2161 15.4196C12.9446 16.1661 13.6223 16.2299 14.4866 16.1022C15.0121 16.0237 16.0973 15.4442 16.3232 14.8058C16.5491 14.1674 16.5491 13.6223 16.4804 13.5094C16.4165 13.3866 16.2348 13.3179 15.9647 13.1853Z" fill="#25D366" />
                                                        </svg>
                                                    </span>
                                                    <Field
                                                        name="whatsappNumber"
                                                        type="text"
                                                        className="w-full pl-10 p-2 border rounded text-font"
                                                        placeholder=" +91 9940012234"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={`${reportDet?.patientDetail?.contactInfo?.whatsappNoDialCode}${reportDet?.patientDetail?.contactInfo?.whatsappNo}`}
                                                        disabled
                                                    />
                                                </div>
                                                {/* {touched.whatsappNumber && errors.whatsappNumber && (
                                                <ErrorMessage name="whatsappNumber" component="div" className="error text-error" />
                                            )} */}
                                            </div>


                                            {/* Report URL Field (Read-Only) */}
                                            <div className="mb-6">
                                                <label className="text-left label-text-font">Report URL</label>
                                                <Field
                                                    name="reportUrl"
                                                    type="text"
                                                    className="p-2 border rounded w-full bg-gray-100 text-font"
                                                    value={`http://3.214.130.93/report/preview/${reportDet?.reportNumber}`}
                                                    disabled
                                                />
                                            </div>

                                            {/* Buttons */}
                                            <div className="modal-actions flex justify-start gap-4">
                                                <button type="button" className="custom-button popup-button" onClick={handleCancel}>Cancel</button>
                                                <button type="submit" className="custom-button main-theme save-btn" >Send</button>
                                            </div>
                                        </div>
                                    </Form>
                                )}
                            </Formik>)
                        }

                    </div>
                </div>
            )}
            {isSuccessOpen && (
                <SuccessModal
                isSuccessModalOpen={isSuccessOpen}
                title={`Report shared successfully.`}
                handleCloseModal={()=>setIsSuccessOpen(false)}
            />
            )}
        </>
    );
};

export default ShareModal;
